"use client";
import React from "react";
import ShopRowGrid from "./ShopRowGrid";
import usePagination from "hooks/usePagination";
import { fetchHomeShops, HOME_SHOP_PAGE_SIZE } from "services/shop/list";
import { useT } from "providers/I18nProvider";
import useRotatingTagline from "hooks/useRotatingTagline";

interface Props {
  title?: string;
  description?: string;
  /** Danh mục chính (type MAIN) để làm chip filter */
  categories?: any[];
  /** Trang 1 do server lấy sẵn (app/[locale]/page.tsx) → không gọi lại khi mount */
  /** Trang 1 server lấy sẵn; null/undefined = server lỗi → tự fetch ở client */
  initialShops?: { data: any[]; total: number } | null;
}

/**
 * Dải "Địa điểm khác" trang chủ. Trang 1 nhận từ props (server render sẵn, Google thấy 12 shop);
 * chỉ gọi API khi người dùng đổi trang hoặc lọc danh mục.
 */
const ShopVerticalSection: React.FC<Props> = ({
  title,
  description,
  categories = [],
  initialShops,
}) => {
  const t = useT();
  const resolvedTitle = title ?? t("home.otherPlaces");
  const resolvedDescription = useRotatingTagline("home.otherPlacesDesc", description);
  const [category, setCategory] = React.useState<string>("");
  // onNextPage/onLoadPage của usePagination chỉ gọi loader với {page, limit}
  // (không có category) — nếu loader đọc category từ tham số, chọn chip rồi
  // sang trang 2 sẽ mất lọc (trả toàn bộ shop). Giữ category hiện tại trong
  // ref (giống containers/OfferListContainer) để loader luôn đọc đúng giá trị
  // mới nhất dù lượt gọi đến từ submit() (có category) hay onNextPage() (không).
  const categoryRef = React.useRef<string>("");
  const pagination = usePagination({
    initialParams: {
      page: 1,
      limit: HOME_SHOP_PAGE_SIZE,
      initialItems: initialShops?.data,
      initialTotal: initialShops?.total ?? 0,
    },
    loader: ({ page, limit, category: cat }: any) => {
      if (cat !== undefined) categoryRef.current = cat;
      return fetchHomeShops({ page, limit, category: categoryRef.current });
    },
  });

  // Không có dữ liệu server (lỗi CMS lúc render) → tự tải lần đầu như trước
  React.useEffect(() => {
    if (!initialShops) pagination?.onReload?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeCategory = (id: string) => {
    setCategory(id);
    pagination.submit({ category: id });
  };

  return (
    <ShopRowGrid
      pagination={pagination}
      title={resolvedTitle}
      description={resolvedDescription}
      categories={categories}
      category={category}
      onChangeCategory={onChangeCategory}
    />
  );
};

export default ShopVerticalSection;
